import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Clock } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';
import Button from './components/Button';

import { devLog } from '../../../utils/devLog';
const Contact = () => {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        devLog.log('Contact form submitted:', formData);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        }, 3000);
    };

    const inputClass = "w-full px-4 py-2.5 md:py-3 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#2D6A4F] transition-colors bg-white";

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header cartCount={3} />

            {/* Hero */}
            <section className="bg-[#2D6A4F] text-white py-12 md:py-20">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-semibold mb-3">Get In Touch</h1>
                    <p className="text-sm md:text-base text-white/70 max-w-lg mx-auto">
                        Have a question about our collections or need styling advice? We would love to hear from you.
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-16">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Form */}
                    <div className="lg:col-span-2">
                        <div className="border border-[#E5E7EB] p-5 md:p-8">
                            <h2 className="text-lg md:text-xl font-semibold text-[#1A1A1A] mb-5">Send Us a Message</h2>

                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Send size={28} className="text-white" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">Message Sent!</h3>
                                    <p className="text-sm text-[#6B7280]">Thank you for reaching out. We will get back to you within 24 hours.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Your Name *</label>
                                            <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className={inputClass} placeholder="Full name" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Email *</label>
                                            <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className={inputClass} placeholder="you@example.com" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Phone</label>
                                            <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} placeholder="+91 98765 43210" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Subject *</label>
                                            <select name="subject" value={formData.subject} onChange={handleInputChange} required className={inputClass}>
                                                <option value="">Select a subject</option>
                                                <option value="general">General Inquiry</option>
                                                <option value="custom">Custom Design Request</option>
                                                <option value="order">Order Support</option>
                                                <option value="wholesale">Wholesale Inquiry</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Message *</label>
                                        <textarea name="message" value={formData.message} onChange={handleInputChange} required rows="5"
                                            className="w-full px-4 py-2.5 md:py-3 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#2D6A4F] transition-colors resize-none"
                                            placeholder="Tell us about your inquiry..." />
                                    </div>
                                    <Button type="submit" variant="primary" size="lg" fullWidth>
                                        <Send size={16} className="mr-2" /> Send Message
                                    </Button>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-5">
                        <div className="border border-[#E5E7EB] p-5">
                            <h3 className="text-base font-semibold text-[#1A1A1A] mb-4">Contact Information</h3>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 bg-[#F8FAF8] rounded-full flex items-center justify-center flex-shrink-0">
                                        <Mail className="text-[#2D6A4F]" size={16} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-[#1A1A1A]">Email</h4>
                                        <a href="mailto:hello@priya.com" className="text-sm text-[#6B7280] hover:text-[#2D6A4F]">hello@priya.com</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 bg-[#F8FAF8] rounded-full flex items-center justify-center flex-shrink-0">
                                        <Phone className="text-[#2D6A4F]" size={16} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-[#1A1A1A]">Phone</h4>
                                        <a href="tel:+919876543210" className="text-sm text-[#6B7280] hover:text-[#2D6A4F]">+91 98765 43210</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 bg-[#F8FAF8] rounded-full flex items-center justify-center flex-shrink-0">
                                        <MapPin className="text-[#2D6A4F]" size={16} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-[#1A1A1A]">Studio</h4>
                                        <p className="text-sm text-[#6B7280]">42 Fashion Lane<br />Mumbai, Maharashtra 400001<br />India</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#2D6A4F] p-5 text-white">
                            <div className="flex items-center gap-2 mb-3">
                                <Clock size={18} />
                                <h3 className="text-base font-semibold">Business Hours</h3>
                            </div>
                            <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between"><span className="text-white/70">Monday - Friday</span><span className="font-medium">10:00 AM - 7:00 PM</span></div>
                                <div className="flex justify-between"><span className="text-white/70">Saturday</span><span className="font-medium">10:00 AM - 5:00 PM</span></div>
                                <div className="flex justify-between"><span className="text-white/70">Sunday</span><span className="font-medium">Closed</span></div>
                            </div>
                        </div>

                        <div className="border border-[#E5E7EB] p-5">
                            <h3 className="text-base font-semibold text-[#1A1A1A] mb-3">Follow Us</h3>
                            <div className="flex gap-3">
                                <a href="#" className="w-10 h-10 bg-[#F8FAF8] rounded-full flex items-center justify-center hover:bg-[#2D6A4F] hover:text-white text-[#2D6A4F] transition-all">
                                    <Instagram size={18} />
                                </a>
                                <a href="#" className="w-10 h-10 bg-[#F8FAF8] rounded-full flex items-center justify-center hover:bg-[#2D6A4F] hover:text-white text-[#2D6A4F] transition-all">
                                    <Facebook size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Contact;
